import React, { useState, useRef, cloneElement } from 'react';
import { CheckCircleIcon, SaveIcon, SearchIcon, FilterIcon, ChevronUpIcon, ChevronDownIcon, UsersIcon, BuildingIcon, PlusIcon, ArrowUpIcon, ArrowDownIcon, XIcon, HelpCircleIcon, MailIcon, ImageIcon, UploadIcon, PaletteIcon, TypeIcon } from 'lucide-react';
interface CompanyProfile {
  legalName: string;
  dbaName: string;
  taxId: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  businessHours: string;
  timeZone: string;
  primaryContact: string;
  phone: string;
  email: string;
}
interface User {
  id: string;
  name: string;
  email: string;
  role: 'Admin' | 'Manager' | 'User';
  status: 'Active' | 'Inactive' | 'Pending';
  lastLogin: string;
}
interface NewUser {
  firstName: string;
  lastName: string;
  email: string;
  role: 'Administrator' | 'Editor' | 'Viewer';
}
const roleDescriptions = {
  Administrator: 'Full access to all settings and user management',
  Editor: 'Can view loads and submit documents but not invite users',
  Viewer: 'Read-only access to loads and dashboards'
};
const googleFonts = ['Inter', 'Roboto', 'Open Sans', 'Lato', 'Poppins', 'Montserrat', 'Source Sans Pro', 'Raleway', 'Ubuntu', 'Nunito'];
const BrandingSettings = {
  logo: null,
  primaryColor: '#d52724',
  secondaryColor: '#171717',
  font: 'Inter'
};
const OrganizationSettings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [sortField, setSortField] = useState<keyof User>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [profile, setProfile] = useState<CompanyProfile>({
    legalName: 'Acme Logistics Inc.',
    dbaName: 'Acme Transport',
    taxId: '12-3456789',
    address: '123 Corporate Drive',
    city: 'Chicago',
    state: 'IL',
    zip: '60601',
    country: 'United States',
    businessHours: '9:00 AM - 5:00 PM CST',
    timeZone: 'America/Chicago',
    primaryContact: 'John Smith',
    phone: '(312) 555-0123',
    email: 'john.smith@acmelogistics.com'
  });
  // Mock user data
  const users: User[] = [{
    id: '1',
    name: 'John Smith',
    email: 'john.smith@acmelogistics.com',
    role: 'Admin',
    status: 'Active',
    lastLogin: '2024-01-15T10:30:00Z'
  }, {
    id: '2',
    name: 'Sarah Johnson',
    email: 'sarah.j@acmelogistics.com',
    role: 'Manager',
    status: 'Active',
    lastLogin: '2024-01-15T09:15:00Z'
  }, {
    id: '3',
    name: 'Mike Wilson',
    email: 'm.wilson@acmelogistics.com',
    role: 'User',
    status: 'Inactive',
    lastLogin: '2024-01-10T14:20:00Z'
  }
  // Add more mock users as needed
  ];
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [newUser, setNewUser] = useState<NewUser>({
    firstName: '',
    lastName: '',
    email: '',
    role: 'Viewer'
  });
  const [showRoleTooltip, setShowRoleTooltip] = useState<string | null>(null);
  const [branding, setBranding] = useState<BrandingSettings>({
    logo: null,
    primaryColor: '#d52724',
    secondaryColor: '#171717',
    font: 'Inter'
  });
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };
  const handleFile = (file: File) => {
    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a PNG, JPG, or SVG file');
      return;
    }
    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('File size must be less than 2MB');
      return;
    }
    // Create URL for preview
    const reader = new FileReader();
    reader.onload = e => {
      setBranding(prev => ({
        ...prev,
        logo: e.target?.result as string
      }));
    };
    reader.readAsDataURL(file);
  };
  const handleColorChange = (color: string, type: 'primary' | 'secondary') => {
    setBranding(prev => ({
      ...prev,
      [type === 'primary' ? 'primaryColor' : 'secondaryColor']: color
    }));
  };
  const handleFontChange = (font: string) => {
    setBranding(prev => ({
      ...prev,
      font
    }));
  };
  const handleBrandingSave = async () => {
    setLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setShowSuccess(true);
    setLoading(false);
    setTimeout(() => setShowSuccess(false), 3000);
  };
  const handleInputChange = (field: keyof CompanyProfile, value: string) => {
    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setShowSuccess(true);
    setLoading(false);
    setTimeout(() => {
      setShowSuccess(false);
    }, 3000);
  };
  const handleSort = (field: keyof User) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const toggleRole = (role: string) => {
    setSelectedRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
  };
  const toggleStatus = (status: string) => {
    setSelectedStatuses(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]);
  };
  // Filter and sort users
  const filteredUsers = users.filter(user => {
    const matchesSearch = searchQuery === '' || user.name.toLowerCase().includes(searchQuery.toLowerCase()) || user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = selectedRoles.length === 0 || selectedRoles.includes(user.role);
    const matchesStatus = selectedStatuses.length === 0 || selectedStatuses.includes(user.status);
    return matchesSearch && matchesRole && matchesStatus;
  }).sort((a, b) => {
    if (a[sortField] < b[sortField]) return sortDirection === 'asc' ? -1 : 1;
    if (a[sortField] > b[sortField]) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddingUser(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    // Add the new user to the list
    const user: User = {
      id: (users.length + 1).toString(),
      name: `${newUser.firstName} ${newUser.lastName}`,
      email: newUser.email,
      role: newUser.role === 'Administrator' ? 'Admin' : newUser.role === 'Editor' ? 'Manager' : 'User',
      status: 'Pending',
      lastLogin: new Date().toISOString()
    };
    users.push(user);
    setAddingUser(false);
    setShowAddUserModal(false);
    setNewUser({
      firstName: '',
      lastName: '',
      email: '',
      role: 'Viewer'
    });
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };
  const StatusBadge: React.FC<{
    status: User['status'];
  }> = ({
    status
  }) => {
    const styles = {
      Active: 'bg-green-100 text-green-800',
      Inactive: 'bg-gray-100 text-gray-800',
      Pending: 'bg-amber-100 text-amber-800'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`} data-id="element-728">
        {status}
      </span>;
  };
  return <div className="py-4 max-w-[1400px] mx-auto" data-id="element-729">
      <div className="mb-6 flex items-center justify-between" data-id="element-730">
        <div data-id="element-731">
          <h1 className="text-2xl font-semibold text-gray-800" data-id="element-732">
            Organization Settings
          </h1>
          <p className="text-gray-600" data-id="element-733">
            Manage your organization profile and user accounts
          </p>
        </div>
        {/* Contextual help button */}
        <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-734">
          <HelpCircleIcon size={16} className="mr-2" data-id="element-735" />
          Help Guide
        </button>
      </div>
      {showSuccess && <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between" data-id="element-736">
          <div className="flex items-center" data-id="element-737">
            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-738" />
            <p className="text-green-700 font-medium" data-id="element-739">
              Changes saved successfully
            </p>
          </div>
          <button onClick={() => setShowSuccess(false)} className="text-green-600 hover:text-green-700" data-id="element-740">
            <XIcon size={16} data-id="element-741" />
          </button>
        </div>}
      {/* Enhanced tabs with better visual hierarchy */}
      <div className="mb-6 border-b border-gray-200" data-id="element-742">
        <nav className="-mb-px flex space-x-8" aria-label="Settings sections" data-id="element-743">
          {[{
          id: 'profile',
          icon: <BuildingIcon size={20} data-id="element-744" />,
          label: 'Company Profile'
        }, {
          id: 'branding',
          icon: <PaletteIcon size={20} data-id="element-745" />,
          label: 'Branding'
        }, {
          id: 'users',
          icon: <UsersIcon size={20} data-id="element-746" />,
          label: 'User Management'
        }].map(({
          id,
          icon,
          label
        }) => <button key={id} onClick={() => setActiveTab(id)} className={`
                py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm
                ${activeTab === id ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                transition-colors duration-150
              `} data-id="element-747">
              {cloneElement(icon, {
            className: 'mr-2'
          })}
              {label}
            </button>)}
        </nav>
      </div>
      {activeTab === 'profile' ?
    // Company Profile Form (existing code)
    <div className="bg-white rounded-lg shadow-sm" data-id="element-748">
          <div className="p-6 border-b border-gray-200" data-id="element-749">
            <h2 className="text-lg font-medium text-gray-900 flex items-center" data-id="element-750">
              <BuildingIcon className="mr-2 h-5 w-5 text-primary-600" data-id="element-751" />
              Company Profile
            </h2>
            <p className="mt-1 text-sm text-gray-500" data-id="element-752">
              Update your organization's basic information and settings
            </p>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-6" data-id="element-753">
            <div className="space-y-4" data-id="element-754">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-755">
                <div data-id="element-756">
                  <label htmlFor="legalName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-757">
                    Legal Company Name *
                  </label>
                  <input type="text" id="legalName" value={profile.legalName} onChange={e => handleInputChange('legalName', e.target.value)} maxLength={100} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm" data-id="element-758" />
                </div>
                <div data-id="element-759">
                  <label htmlFor="dbaName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-760">
                    DBA Name
                  </label>
                  <input type="text" id="dbaName" value={profile.dbaName} onChange={e => handleInputChange('dbaName', e.target.value)} maxLength={100} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-gray-900 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-761" />
                </div>
                <div data-id="element-762">
                  <label htmlFor="taxId" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-763">
                    Tax ID/EIN *
                  </label>
                  <input type="text" id="taxId" value={profile.taxId} onChange={e => handleInputChange('taxId', e.target.value)} maxLength={20} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-gray-900 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-764" />
                </div>
              </div>
              {/* Address Information */}
              <div data-id="element-765">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-766">
                  Street Address *
                </label>
                <input type="text" id="address" value={profile.address} onChange={e => handleInputChange('address', e.target.value)} maxLength={100} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-gray-900 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-767" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-768">
                <div data-id="element-769">
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-770">
                    City *
                  </label>
                  <input type="text" id="city" value={profile.city} onChange={e => handleInputChange('city', e.target.value)} maxLength={50} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-gray-900 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-771" />
                </div>
                <div data-id="element-772">
                  <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-773">
                    State/Province *
                  </label>
                  <input type="text" id="state" value={profile.state} onChange={e => handleInputChange('state', e.target.value)} maxLength={50} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-gray-900 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-774" />
                </div>
                <div data-id="element-775">
                  <label htmlFor="zip" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-776">
                    ZIP/Postal Code *
                  </label>
                  <input type="text" id="zip" value={profile.zip} onChange={e => handleInputChange('zip', e.target.value)} maxLength={20} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-gray-900 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-777" />
                </div>
              </div>
              {/* Business Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-778">
                <div data-id="element-779">
                  <label htmlFor="businessHours" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-780">
                    Business Hours
                  </label>
                  <input type="text" id="businessHours" value={profile.businessHours} onChange={e => handleInputChange('businessHours', e.target.value)} maxLength={100} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-gray-900 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-781" />
                </div>
                <div data-id="element-782">
                  <label htmlFor="timeZone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-783">
                    Time Zone *
                  </label>
                  <select id="timeZone" value={profile.timeZone} onChange={e => handleInputChange('timeZone', e.target.value)} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-gray-900 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-784">
                    <option value="America/New_York" data-id="element-785">Eastern Time (ET)</option>
                    <option value="America/Chicago" data-id="element-786">Central Time (CT)</option>
                    <option value="America/Denver" data-id="element-787">Mountain Time (MT)</option>
                    <option value="America/Los_Angeles" data-id="element-788">
                      Pacific Time (PT)
                    </option>
                  </select>
                </div>
                <div data-id="element-789">
                  <label htmlFor="primaryContact" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-790">
                    Primary Contact *
                  </label>
                  <input type="text" id="primaryContact" value={profile.primaryContact} onChange={e => handleInputChange('primaryContact', e.target.value)} maxLength={100} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-gray-900 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-791" />
                </div>
                <div data-id="element-792">
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-793">
                    Phone Number *
                  </label>
                  <input type="tel" id="phone" value={profile.phone} onChange={e => handleInputChange('phone', e.target.value)} maxLength={20} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-gray-900 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-794" />
                </div>
                <div data-id="element-795">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-796">
                    Email Address *
                  </label>
                  <input type="email" id="email" value={profile.email} onChange={e => handleInputChange('email', e.target.value)} maxLength={100} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-gray-900 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-797" />
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3" data-id="element-798">
              <button type="button" className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-799">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-300 disabled:cursor-not-allowed" data-id="element-800">
                {loading ? <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" data-id="element-801">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-802" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-803" />
                    </svg>
                    Saving...
                  </> : <>
                    <SaveIcon className="h-4 w-4 mr-1.5" data-id="element-804" />
                    Save Changes
                  </>}
              </button>
            </div>
          </form>
        </div> : activeTab === 'users' ?
    // User Management Section
    <div className="space-y-6" data-id="element-805">
          {/* Enhanced user management filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-806">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0" data-id="element-807">
              <div className="flex items-center space-x-4 flex-1" data-id="element-808">
                <div className="relative flex-1 max-w-md" data-id="element-809">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-810">
                    <SearchIcon size={16} className="text-gray-400" data-id="element-811" />
                  </div>
                  <input type="text" placeholder="Search users..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500" data-id="element-812" />
                </div>
                {/* Add User button */}
                <button onClick={() => setShowAddUserModal(true)} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-700 hover:bg-primary-800" data-id="element-813">
                  <PlusIcon size={16} className="mr-2" data-id="element-814" />
                  Add User
                </button>
              </div>
            </div>
            {/* Expanded filters */}
            {showFilters && <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-815">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-816">
                  <div data-id="element-817">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-818">
                      Role
                    </label>
                    <div className="space-y-2" data-id="element-819">
                      {['Admin', 'Manager', 'User'].map(role => <div key={role} className="flex items-center" data-id="element-820">
                          <input type="checkbox" id={`role-${role}`} checked={selectedRoles.includes(role)} onChange={() => toggleRole(role)} className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" data-id="element-821" />
                          <label htmlFor={`role-${role}`} className="ml-2 text-sm text-gray-700" data-id="element-822">
                            {role}
                          </label>
                        </div>)}
                    </div>
                  </div>
                  <div data-id="element-823">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-824">
                      Status
                    </label>
                    <div className="space-y-2" data-id="element-825">
                      {['Active', 'Inactive', 'Pending'].map(status => <div key={status} className="flex items-center" data-id="element-826">
                          <input type="checkbox" id={`status-${status}`} checked={selectedStatuses.includes(status)} onChange={() => toggleStatus(status)} className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" data-id="element-827" />
                          <label htmlFor={`status-${status}`} className="ml-2 text-sm text-gray-700" data-id="element-828">
                            {status}
                          </label>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>}
          </div>
          {/* Enhanced user table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-829">
            <div className="overflow-x-auto" data-id="element-830">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-831">
                <thead className="bg-gray-50" data-id="element-832">
                  <tr data-id="element-833">
                    {(['name', 'email', 'role', 'status', 'lastLogin'] as const).map(field => <th key={field} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700" onClick={() => handleSort(field)} data-id="element-834">
                        <div className="flex items-center" data-id="element-835">
                          {field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')}
                          {sortField === field && (sortDirection === 'asc' ? <ArrowUpIcon size={14} className="ml-1" data-id="element-836" /> : <ArrowDownIcon size={14} className="ml-1" data-id="element-837" />)}
                        </div>
                      </th>)}
                    <th scope="col" className="relative px-6 py-3" data-id="element-838">
                      <span className="sr-only" data-id="element-839">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-840">
                  {paginatedUsers.map(user => <tr key={user.id} className="hover:bg-gray-50" data-id="element-841">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-842">
                        {user.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-843">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-844">
                        {user.role}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-845">
                        <StatusBadge status={user.status} data-id="element-846" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-847">
                        {new Date(user.lastLogin).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-848">
                        <button className="text-primary-600 hover:text-primary-900" data-id="element-849">
                          Edit
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {totalPages > 1 && <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6" data-id="element-850">
                <div className="flex-1 flex justify-between sm:hidden" data-id="element-851">
                  <button onClick={() => setCurrentPage(page => Math.max(1, page - 1))} disabled={currentPage === 1} className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400" data-id="element-852">
                    Previous
                  </button>
                  <button onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))} disabled={currentPage === totalPages} className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400" data-id="element-853">
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-854">
                  <div data-id="element-855">
                    <p className="text-sm text-gray-700" data-id="element-856">
                      Showing page {currentPage} of {totalPages}
                    </p>
                  </div>
                  <div data-id="element-857">
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" data-id="element-858">
                      <button onClick={() => setCurrentPage(page => Math.max(1, page - 1))} disabled={currentPage === 1} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400" data-id="element-859">
                        Previous
                      </button>
                      <button onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))} disabled={currentPage === totalPages} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400" data-id="element-860">
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>}
          </div>
          {/* Add User Modal */}
          {showAddUserModal && <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50" data-id="element-861">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" data-id="element-862">
                <div className="px-4 py-5 sm:p-6" data-id="element-863">
                  <div className="flex items-center justify-between mb-4" data-id="element-864">
                    <h3 className="text-lg font-medium text-gray-900" data-id="element-865">
                      Add New User
                    </h3>
                    <button onClick={() => setShowAddUserModal(false)} className="text-gray-400 hover:text-gray-500" data-id="element-866">
                      <XIcon size={20} data-id="element-867" />
                    </button>
                  </div>
                  <form onSubmit={handleAddUser} className="space-y-4" data-id="element-868">
                    <div data-id="element-869">
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700" data-id="element-870">
                        First Name *
                      </label>
                      <input type="text" id="firstName" value={newUser.firstName} onChange={e => setNewUser({
                  ...newUser,
                  firstName: e.target.value
                })} maxLength={50} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm" data-id="element-871" />
                    </div>
                    <div data-id="element-872">
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700" data-id="element-873">
                        Last Name *
                      </label>
                      <input type="text" id="lastName" value={newUser.lastName} onChange={e => setNewUser({
                  ...newUser,
                  lastName: e.target.value
                })} maxLength={50} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm" data-id="element-874" />
                    </div>
                    <div data-id="element-875">
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700" data-id="element-876">
                        Email Address *
                      </label>
                      <input type="email" id="email" value={newUser.email} onChange={e => setNewUser({
                  ...newUser,
                  email: e.target.value
                })} maxLength={100} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm" data-id="element-877" />
                    </div>
                    <div data-id="element-878">
                      <label className="block text-sm font-medium text-gray-700" data-id="element-879">
                        Role *
                      </label>
                      <div className="mt-1 space-y-2" data-id="element-880">
                        {Object.entries(roleDescriptions).map(([role, description]) => <div key={role} className="relative flex items-start" data-id="element-881">
                              <div className="flex items-center h-5" data-id="element-882">
                                <input type="radio" name="role" value={role} checked={newUser.role === role} onChange={e => setNewUser({
                        ...newUser,
                        role: e.target.value as NewUser['role']
                      })} className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500" data-id="element-883" />
                              </div>
                              <div className="ml-3" data-id="element-884">
                                <label className="text-sm font-medium text-gray-700 flex items-center" data-id="element-885">
                                  {role}
                                  <button type="button" onMouseEnter={() => setShowRoleTooltip(role)} onMouseLeave={() => setShowRoleTooltip(null)} className="ml-1 text-gray-400 hover:text-gray-500" data-id="element-886">
                                    <HelpCircleIcon size={16} data-id="element-887" />
                                  </button>
                                </label>
                                {showRoleTooltip === role && <div className="absolute left-full ml-2 w-64 bg-gray-900 text-white text-xs rounded py-1 px-2" data-id="element-888">
                                    {description}
                                  </div>}
                              </div>
                            </div>)}
                      </div>
                    </div>
                    <div className="mt-5 sm:mt-6 flex justify-end space-x-3" data-id="element-889">
                      <button type="button" onClick={() => setShowAddUserModal(false)} className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500" data-id="element-890">
                        Cancel
                      </button>
                      <button type="submit" disabled={addingUser} className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-700 hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-300" data-id="element-891">
                        {addingUser ? <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" data-id="element-892">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-893" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-894" />
                            </svg>
                            Sending Invitation...
                          </> : <>
                            <MailIcon size={16} className="mr-2" data-id="element-895" />
                            Invite User
                          </>}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>}
        </div> :
    // Branding Section
    <div className="space-y-6" data-id="element-896">
          <div className="bg-white rounded-lg shadow-sm" data-id="element-897">
            <div className="p-6 border-b border-gray-200" data-id="element-898">
              <h2 className="text-lg font-medium text-gray-900 flex items-center" data-id="element-899">
                <PaletteIcon className="mr-2 h-5 w-5 text-primary-600" data-id="element-900" />
                Portal Branding
              </h2>
              <p className="mt-1 text-sm text-gray-500" data-id="element-901">
                Customize the look and feel of your portal
              </p>
            </div>
            <div className="p-6" data-id="element-902">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-903">
                {/* Settings Panel */}
                <div className="space-y-6" data-id="element-904">
                  {/* Logo Upload */}
                  <div data-id="element-905">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-906">
                      Company Logo
                    </label>
                    <div className={`border-2 border-dashed rounded-lg p-6 text-center ${dragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300'}`} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} data-id="element-907">
                      <input type="file" ref={fileInputRef} onChange={e => e.target.files && handleFile(e.target.files[0])} accept=".png,.jpg,.jpeg,.svg" className="hidden" data-id="element-908" />
                      <div className="space-y-2" data-id="element-909">
                        <div className="flex justify-center" data-id="element-910">
                          <UploadIcon className="h-10 w-10 text-gray-400" data-id="element-911" />
                        </div>
                        <div className="text-sm text-gray-600" data-id="element-912">
                          <button type="button" onClick={() => fileInputRef.current?.click()} className="text-primary-600 hover:text-primary-500 font-medium" data-id="element-913">
                            Click to upload
                          </button>
                          {' or drag and drop'}
                        </div>
                        <p className="text-xs text-gray-500" data-id="element-914">
                          PNG, JPG or SVG (max. 2MB) <br data-id="element-915" />
                          Recommended size: 240px x 60px
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Color Selection */}
                  <div className="space-y-4" data-id="element-916">
                    <div data-id="element-917">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-918">
                        Primary Brand Color
                      </label>
                      <input type="color" value={branding.primaryColor} onChange={e => handleColorChange(e.target.value, 'primary')} className="h-10 w-full rounded-md cursor-pointer" data-id="element-919" />
                    </div>
                    <div data-id="element-920">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-921">
                        Secondary Brand Color
                      </label>
                      <input type="color" value={branding.secondaryColor} onChange={e => handleColorChange(e.target.value, 'secondary')} className="h-10 w-full rounded-md cursor-pointer" data-id="element-922" />
                    </div>
                  </div>
                  {/* Font Selection */}
                  <div data-id="element-923">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-924">
                      Default Font
                    </label>
                    <select value={branding.font} onChange={e => handleFontChange(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 rounded-md" data-id="element-925">
                      {googleFonts.map(font => <option key={font} value={font} data-id="element-926">
                          {font}
                        </option>)}
                    </select>
                  </div>
                  {/* Save Button */}
                  <div className="flex justify-end" data-id="element-927">
                    <button onClick={handleBrandingSave} disabled={loading} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-700 hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-300" data-id="element-928">
                      {loading ? <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" data-id="element-929">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-930" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-931" />
                          </svg>
                          Saving...
                        </> : <>
                          <SaveIcon className="h-4 w-4 mr-1.5" data-id="element-932" />
                          Save Changes
                        </>}
                    </button>
                  </div>
                </div>
                {/* Live Preview Panel */}
                <div className="sticky top-6" data-id="element-933">
                  <h3 className="text-sm font-medium text-gray-900 mb-4" data-id="element-934">
                    Live Preview
                  </h3>
                  <div className="border rounded-lg bg-white shadow-sm overflow-hidden" data-id="element-935">
                    {/* Header Preview */}
                    <div className="p-4 border-b" style={{
                  backgroundColor: branding.primaryColor
                }} data-id="element-936">
                      {branding.logo ? <img src={branding.logo} alt="Company logo" className="h-8 w-auto" data-id="element-937" /> : <div className="h-8 w-32 bg-gray-200 rounded animate-pulse" data-id="element-938" />}
                    </div>
                    {/* Content Preview */}
                    <div className="p-4" data-id="element-939">
                      <div className="space-y-4" style={{
                    fontFamily: branding.font
                  }} data-id="element-940">
                        <div className="h-4 w-2/3 bg-gray-200 rounded" data-id="element-941" />
                        <div className="h-4 w-3/4 bg-gray-200 rounded" data-id="element-942" />
                        <div className="h-4 w-1/2 bg-gray-200 rounded" data-id="element-943" />
                        <button className="px-4 py-2 rounded-md text-white text-sm" style={{
                      backgroundColor: branding.primaryColor
                    }} data-id="element-944">
                          Sample Button
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};
export default OrganizationSettings;